/**
 * ExecutionErrorsPanel - Renders structured execution error details
 *
 * Shows the top-level ``error_message`` once (when present) and groups
 * structured ``error_details`` by ``query_name`` (falling back to
 * ``error_type`` when ``query_name`` is missing).
 *
 * Renders a friendly empty state when status is failed/partial but no
 * structured errors are available (only the top-level message is shown).
 */
import { AlertCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ExecutionErrorDetail } from '@/types/connectors';

export interface ExecutionErrorsPanelProps {
  errorDetails?: ExecutionErrorDetail[] | null;
  errorMessage?: string | null;
  className?: string;
}

function variantForErrorType(
  errorType: string
): 'destructive' | 'secondary' | 'outline' {
  const lower = (errorType || '').toLowerCase();
  if (lower.includes('warn') || lower.includes('partial')) return 'secondary';
  if (lower.includes('skip')) return 'outline';
  return 'destructive';
}

function iconForErrorType(errorType: string) {
  const lower = (errorType || '').toLowerCase();
  if (lower.includes('warn') || lower.includes('partial')) {
    return <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />;
  }
  if (lower.includes('skip')) {
    return <AlertCircle className="h-4 w-4 text-muted-foreground" aria-hidden />;
  }
  return <XCircle className="h-4 w-4 text-destructive" aria-hidden />;
}

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function groupErrors(
  errors: ExecutionErrorDetail[]
): Array<{ key: string; items: ExecutionErrorDetail[] }> {
  const groups = new Map<string, ExecutionErrorDetail[]>();
  for (const err of errors) {
    const key = err.query_name || err.error_type || 'Unknown';
    const existing = groups.get(key);
    if (existing) {
      existing.push(err);
    } else {
      groups.set(key, [err]);
    }
  }
  return Array.from(groups.entries()).map(([key, items]) => ({ key, items }));
}

function isErrorDetailArray(value: unknown): value is ExecutionErrorDetail[] {
  return (
    Array.isArray(value) &&
    value.every(
      (v) =>
        v &&
        typeof v === 'object' &&
        'error_type' in (v as Record<string, unknown>) &&
        'error_message' in (v as Record<string, unknown>)
    )
  );
}

export function ExecutionErrorsPanel({
  errorDetails,
  errorMessage,
  className,
}: ExecutionErrorsPanelProps) {
  const normalized: ExecutionErrorDetail[] = isErrorDetailArray(errorDetails)
    ? errorDetails
    : [];

  const hasStructured = normalized.length > 0;
  const hasMessage = !!errorMessage && errorMessage.trim().length > 0;

  if (!hasStructured && !hasMessage) {
    return (
      <div
        className={cn(
          'rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground',
          className
        )}
        data-testid="execution-errors-empty"
      >
        No error details available for this execution.
      </div>
    );
  }

  const groups = hasStructured ? groupErrors(normalized) : [];

  return (
    <div className={cn('space-y-3', className)} data-testid="execution-errors-panel">
      {hasMessage && (
        <div
          className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
          data-testid="execution-error-message"
        >
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden />
            <p className="font-medium break-words">{errorMessage}</p>
          </div>
        </div>
      )}

      {hasStructured && (
        <div className="space-y-3" data-testid="execution-error-groups">
          {groups.map((group) => (
            <div
              key={group.key}
              className="rounded-md border bg-card"
              data-testid="execution-error-group"
            >
              <div className="flex items-center justify-between border-b px-3 py-2">
                <div className="font-medium text-sm truncate" title={group.key}>
                  {group.key}
                </div>
                <Badge variant="outline" className="text-xs">
                  {group.items.length} error{group.items.length === 1 ? '' : 's'}
                </Badge>
              </div>
              <ul className="divide-y">
                {group.items.map((err, idx) => (
                  <li
                    key={`${group.key}-${idx}`}
                    className="px-3 py-2 space-y-1"
                    data-testid="execution-error-item"
                  >
                    <div className="flex items-center gap-2">
                      {iconForErrorType(err.error_type)}
                      <Badge variant={variantForErrorType(err.error_type)} className="text-xs">
                        {err.error_type}
                      </Badge>
                      {err.account_id && (
                        <span className="text-xs text-muted-foreground">
                          account: {err.account_id}
                        </span>
                      )}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {formatTimestamp(err.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {err.error_message}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExecutionErrorsPanel;
